import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { Modal } from 'components/UI/Modal/Modal'
import { ISelectItem, Select } from 'components/UI/Select/Select'
import { useEffect, useMemo, useState } from 'react'

export const FB_REMOVE_CELL_VALUE = '__FB_REMOVE_CELL_VALUE__'

export interface ValidateModalData {
  missing: string[]
  found: string[]
  title: string
  itemName: string
  columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
  options: ISelectItem<string>[]
}

export interface ValidateModalProps {
  open: boolean
  onClose: () => void
  onValidate: (data: {
    columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
    changes: ValidateModalRow[]
  }) => void
  data: ValidateModalData | null
}

export interface ValidateModalRow {
  original: string
  mapped?: string
}

export function ValidateModal(props: ValidateModalProps) {
  const [missing, setMissing] = useState<ValidateModalRow[]>([])

  const allMapped = useMemo(() => {
    return !missing.some((item) => !item.mapped)
  }, [missing])

  function handleSave() {
    if (props.data === null) return
    props.onValidate({
      columnName: props.data.columnName,
      changes: missing,
    })
  }

  function handleChange(value: string, original: string) {
    setMissing((prev) =>
      prev.map((m) => (m.original === original ? { ...m, mapped: value } : m))
    )
  }

  useEffect(() => {
    setMissing(
      props.data?.missing.map((item) => ({
        original: item,
        mapped: undefined,
      })) ?? []
    )
  }, [props.data])

  if (props.data === null) {
    return null
  }

  return (
    <Modal
      size="xl"
      open={props.open}
      title={props.data.title}
      description={
        'These items were not found in the database. Please map them to the correct item from the dropdown. Or remove them if they are not needed.'
      }
      onOpenChange={(open) => {
        if (!open) props.onClose()
      }}
      acceptButtonText={'Save Changes'}
      blockAccept={!allMapped}
      onAccept={handleSave}
    >
      <div className="flex flex-row gap-4 h-[400px]">
        <div className="flex-1">
          <h1>Valid Items</h1>
          <ClientDataTable
            data={props.data.found.map((item) => ({
              name: item,
            }))}
            columns={[
              {
                header: props.data.itemName,
                accessorKey: 'name',
              },
            ]}
          />
        </div>

        <div className="flex-1">
          <h1>Invalid Items</h1>
          <ClientDataTable
            data={missing}
            columns={[
              {
                header: 'Original',
                accessorKey: 'original',
                cell: (info) => {
                  const value = info.getValue<string>()
                  if (!value) {
                    return (
                      <span className="text-fg-secondary italic">(empty)</span>
                    )
                  }
                  return value
                },
              },
              {
                header: 'Mapped',
                accessorKey: 'mapped',
                cell: (info) => {
                  const row = info.row.original
                  return (
                    <Select
                      nullSelectionLabel="Select"
                      items={props.data?.options ?? []}
                      selected={row.mapped}
                      onSelect={(value) => handleChange(value, row.original)}
                    />
                  )
                },
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}
